/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AssetTagModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { useDialog } from '../../../../hooks/use-dialog';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { Button } from '../../../button/button';
import { AssetTagChip } from '../components/asset-tag-chip';
import { ConfirmationDialog } from '../../../confirmation-dialog/confirmation-dialog';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { InfoBox } from '../../../info-box/info-box';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';
import { idToPastelHex } from '../../../../utils/color';

type Props = {
  onClose: () => void;
};

const ManageTagsDialog = (props: Props) => {
  const { t } = useTranslation();
  const { project, reloadAssetTags } = React.useContext(ProjectContext);
  const confirmDialog = useDialog();

  const [searchQuery, setSearchQuery] = React.useState('');
  const [editingTagId, setEditingTagId] = React.useState<string | null>(null);
  const [editingValue, setEditingValue] = React.useState('');
  const [tagToDelete, setTagToDelete] = React.useState<AssetTagModel | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  // Filter tags based on search query
  const filteredTags = React.useMemo((): AssetTagModel[] => {
    const tags = project.assetTags ?? [];
    if (!searchQuery.trim()) {
      return project.assetTags;
    }
    const query = searchQuery.toLowerCase();
    return tags.filter((tag) => tag.name.toLowerCase().includes(query));
  }, [project.assetTags, searchQuery]);

  const handleStartEdit = (tag: AssetTagModel) => {
    setEditingTagId(tag.id);
    setEditingValue(tag.name);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditingValue('');
    setError(null);
  };

  const handleSaveEdit = async (tagId: string) => {
    if (!editingValue.trim()) {
      setError(t('manageTagsDialog.errors.emptyName', { ns: 'skyviewAssetMenu' }));
      return;
    }

    // Check for duplicate names
    const isDuplicate = (project?.assetTags ?? []).some(
      (tag) => tag.id !== tagId && tag.name.toLowerCase() === editingValue.trim().toLowerCase(),
    );

    if (isDuplicate) {
      setError(t('manageTagsDialog.errors.duplicateName', { ns: 'skyviewAssetMenu' }));
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      const svc = SkyMapAxiosServiceFactory.instance.createAssetTagServiceV1();
      await svc.patchAssetTag({ path: { assetTagId: tagId }, body: { name: editingValue.trim() } });

      await reloadAssetTags();
      setEditingTagId(null);
      setEditingValue('');
    } catch (err) {
      setError(t('manageTagsDialog.errors.updateFailed', { ns: 'skyviewAssetMenu' }));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteTag = (tag: AssetTagModel) => {
    setTagToDelete(tag);
    confirmDialog.show();
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) {
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);

      const svc = SkyMapAxiosServiceFactory.instance.createAssetTagServiceV1();
      await svc.deleteAssetTag({ path: { assetTagId: tagToDelete.id } });

      await reloadAssetTags();
      setTagToDelete(null);
      confirmDialog.hide();
    } catch (err) {
      setError(t('manageTagsDialog.errors.deleteFailed', { ns: 'skyviewAssetMenu' }));
      confirmDialog.hide();
    } finally {
      setIsUpdating(false);
    }
  };

  const showCreateSuggestion = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return false;
    }
    return !(project.assetTags ?? []).some((tag) => tag.name.trim().toLowerCase() === q);
  }, [project.assetTags, searchQuery]);

  const handleCreateTag = async () => {
    const name = searchQuery.trim();
    if (!name) {
      return;
    }
    try {
      setIsUpdating(true);
      setCreateError(null);
      const svc = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      await svc.createAssetTag({ path: { projectId: project.id }, body: { name } });
      await reloadAssetTags();
      setSearchQuery('');
    } catch (err) {
      setCreateError(t('manageTagsDialog.errors.createFailed', { ns: 'skyviewAssetMenu' }));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Dialog closeIcon={true} height="40vh" position="center" width="30vw" onClose={props.onClose}>
        {{
          header: t('manageTagsDialog.title', { ns: 'skyviewAssetMenu' }),
          content: (
            <Stack spacing={1}>
              <SearchWrapper>
                <TextBox
                  id="tag-search"
                  placeholder={t('tagDialog.searchOrCreatePlaceholder', { ns: 'skyviewAssetMenu' })}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && showCreateSuggestion && !isUpdating) {
                      void handleCreateTag();
                    }
                  }}
                />
                {/* Inline plus icon is not used anymore. Temporary row below handles create. */}
              </SearchWrapper>

              {error && (
                <InfoBox color="red" fontSize="small">
                  {error}
                </InfoBox>
              )}

              {createError && (
                <InfoBox color="red" fontSize="small">
                  {createError}
                </InfoBox>
              )}

              {/* plus icon now shown inline in the search field when creating */}

              {filteredTags.length === 0 && !showCreateSuggestion ? (
                <EmptyMessage>
                  {searchQuery.trim()
                    ? t('manageTagsDialog.noTagsFound', { ns: 'skyviewAssetMenu' })
                    : t('manageTagsDialog.noTagsAvailable', { ns: 'skyviewAssetMenu' })}
                </EmptyMessage>
              ) : (
                <TagsList direction="column" spacing={0.5}>
                  {showCreateSuggestion && (
                    <CreateRow
                      aria-label={t('manageTagsDialog.createButton', {
                        ns: 'skyviewAssetMenu',
                        tagName: searchQuery.trim(),
                      })}
                      data-testid="create-tag-row"
                      role="button"
                      onClick={() => {
                        if (!isUpdating) {
                          void handleCreateTag();
                        }
                      }}
                    >
                      <Stack alignItems="center" direction="row" spacing={0.5}>
                        <PlusRight
                          icon={['fas', 'plus']}
                          title={t('manageTagsDialog.createButton', {
                            ns: 'skyviewAssetMenu',
                            tagName: searchQuery.trim(),
                          })}
                        />
                        <AssetTagChip
                          tag={{
                            name: searchQuery.trim(),
                            bgColor: idToPastelHex('temp-color', 10, 55),
                          }}
                        />
                      </Stack>
                    </CreateRow>
                  )}
                  {filteredTags.map((tag) => (
                    <TagItem key={tag.id}>
                      {editingTagId === tag.id ? (
                        <Stack direction="row" spacing={0.5}>
                          <TextBox
                            autoFocus={true}
                            id={`edit-tag-${tag.id}`}
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                void handleSaveEdit(tag.id);
                              } else if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                          />
                          <Button
                            color="primary"
                            data-testid="save-tag-btn"
                            disabled={isUpdating}
                            loading={isUpdating}
                            title={t('save', { ns: 'common' })}
                            variant="contained"
                            onClick={() => void handleSaveEdit(tag.id)}
                          >
                            <Icon icon={['fas', 'check']} />
                          </Button>
                          <Button
                            data-testid="cancel-edit-btn"
                            disabled={isUpdating}
                            title={t('cancel', { ns: 'common' })}
                            variant="text"
                            onClick={handleCancelEdit}
                          >
                            <Icon icon={['fas', 'times']} />
                          </Button>
                        </Stack>
                      ) : (
                        <Stack alignItems="center" direction="row" justifyContent="space-between">
                          <AssetTagChip tag={{ id: tag.id }} />
                          <Stack direction="row" spacing={0.5}>
                            <ActionButton
                              aria-label={t('update', { ns: 'common' })}
                              data-testid="edit-tag-btn"
                              disabled={isUpdating}
                              title={t('update', { ns: 'common' })}
                              onClick={() => handleStartEdit(tag)}
                            >
                              <Icon icon={['fas', 'pencil']} />
                            </ActionButton>
                            <ActionButton
                              aria-label={t('delete', { ns: 'common' })}
                              data-testid="delete-tag-btn"
                              disabled={isUpdating}
                              title={t('delete', { ns: 'common' })}
                              onClick={() => {
                                handleDeleteTag(tag);
                              }}
                            >
                              <Icon color="red" icon={['fas', 'trash']} />
                            </ActionButton>
                          </Stack>
                        </Stack>
                      )}
                    </TagItem>
                  ))}
                </TagsList>
              )}
            </Stack>
          ),
          footer: {
            right: (
              <Button variant="contained" onClick={props.onClose}>
                {t('close', { ns: 'common' })}
              </Button>
            ),
          },
        }}
      </Dialog>

      {confirmDialog.render((pos) => (
        <ConfirmationDialog
          negative={true}
          pos={pos}
          title={t('manageTagsDialog.confirmDelete.title', {
            ns: 'skyviewAssetMenu',
            tagName: tagToDelete?.name ?? '',
          })}
          onCancel={() => {
            setTagToDelete(null);
            confirmDialog.hide();
          }}
          onConfirm={async () => {
            await handleConfirmDelete();
          }}
        >
          {t('manageTagsDialog.confirmDelete.body', {
            ns: 'skyviewAssetMenu',
            tagName: tagToDelete?.name ?? '',
          })}
        </ConfirmationDialog>
      ))}
    </>
  );
};

const TagsList = styled(Stack)`
  max-height: 400px;
  overflow-y: auto;
  gap: 0.5em;
`;

const TagItem = styled.div`
  padding: 0.75em;
  border: 1px solid ${({ theme }) => theme.color.gray.light};
  border-radius: 4px;
  background: white;

  &:hover {
    background: ${({ theme }) => theme.color.gray.lightest};
  }
`;

// removed unused TagName styled component (name now rendered via AssetTagChip)

const ActionButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.color.gray.medium};
  cursor: pointer;
  padding: 0.25em 0.5em;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({ theme }) => theme.color.gray.dark};
    background: ${({ theme }) => theme.color.gray.light};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyMessage = styled.div`
  padding: 2em;
  text-align: center;
  color: ${({ theme }) => theme.color.gray.medium};
  font-style: italic;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: block;
`;

const CreateRow = styled.div`
  padding: 0.75em;
  border: 1px dashed ${({ theme }) => theme.color.gray.light};
  border-radius: 4px;
  background: white;
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.gray.lightest};
  }
`;

const PlusRight = styled(Icon)`
  color: ${({ theme }) => theme.color.gray.medium};
  opacity: 0.6;
`;

export { ManageTagsDialog };
